import { formatTimestamp, type TranscriptSegment } from '@sl/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { meetingsApi } from '../lib/api.js';
import { classifyWordConfidence, needsCheck } from '../lib/confidence.js';

/**
 * The transcript itself — the lines, the low-confidence marking and the
 * in-place editing. Split out of the meeting page because it now appears in
 * two places: as its own tab, and beside the minutes on a wide screen, where
 * it scrolls inside its own container so that following a citation does not
 * move the editor.
 */

export function TranscriptPane({
  meetingId,
  segments,
  canWrite,
  activeSegmentId,
  onSeek,
  /** True in the side pane: the list gets its own scrollbar. */
  contained = false,
}: {
  meetingId: string;
  segments: TranscriptSegment[];
  canWrite: boolean;
  activeSegmentId: string | null;
  onSeek: (time: number, segmentId: string) => void;
  contained?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const saveSegment = useMutation({
    mutationFn: (vars: { segId: string; text: string }) =>
      meetingsApi.updateSegment(meetingId, vars.segId, { text: vars.text }),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['transcript', meetingId] });
    },
  });

  return (
    <div className={`card transcript-list${contained ? ' transcript-contained' : ''}`}>
      {segments.map((segment) => (
        <div
          key={segment.id}
          id={`segment-${segment.id}`}
          className={`segment ${activeSegmentId === segment.id ? 'active' : ''}`}
          onClick={() => onSeek(segment.startSec, segment.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSeek(segment.startSec, segment.id);
          }}
        >
          <div className="time" dir="ltr">
            {formatTimestamp(segment.startSec)}
            {needsCheck(segment.confidence) && (
              <span className="check-marker" title={t('meeting.checkMarkerTitle') as string}>
                ●
              </span>
            )}
          </div>
          <div>
            <div className="speaker">{segment.speaker}</div>
            {editing === segment.id ? (
              <div onClick={(e) => e.stopPropagation()} role="presentation">
                <textarea dir="auto" value={draft} onChange={(e) => setDraft(e.target.value)} />
                <div className="row" style={{ marginTop: 6 }}>
                  <button
                    type="button"
                    className="gold"
                    disabled={saveSegment.isPending}
                    onClick={() => saveSegment.mutate({ segId: segment.id, text: draft })}
                  >
                    {t('common.save')}
                  </button>
                  <button type="button" className="secondary" onClick={() => setEditing(null)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div
                dir="auto"
                onDoubleClick={() => {
                  if (!canWrite) return;
                  setEditing(segment.id);
                  setDraft(segment.text);
                }}
              >
                {segment.words && segment.words.length > 0
                  ? // Plain inline spans, never a flex/grid row: the browser's
                    // own bidi algorithm must keep mixed AR/EN word order.
                    segment.words.map((w, i) => (
                      <Fragment key={i}>
                        {i > 0 ? ' ' : ''}
                        <span
                          className={
                            'word' +
                            (classifyWordConfidence(w.p) === 'verylow'
                              ? ' word-verylow'
                              : classifyWordConfidence(w.p) === 'low'
                                ? ' word-low'
                                : '')
                          }
                          title={
                            needsCheck(w.p)
                              ? (t('meeting.lowConfidenceWordTitle') as string)
                              : undefined
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            onSeek(Math.max(0, w.s - 0.5), segment.id);
                          }}
                        >
                          {w.t}
                        </span>
                      </Fragment>
                    ))
                  : // An edited line has no words (PATCH clears them), so it
                    // falls back to plain text.
                    segment.text}
                {canWrite && (
                  <button
                    type="button"
                    className="link"
                    style={{ marginInlineStart: 8 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(segment.id);
                      setDraft(segment.text);
                    }}
                  >
                    {t('meeting.edit')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
